import React, { useState } from 'react';
import { Mail, User, ArrowRight, Loader2, Lock, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { loginApi, registerApi, sendEmailCodeApi, StoredUser } from '../lib/api';

interface AuthProps {
  onLogin: (user: StoredUser) => void;
}

const Auth: React.FC<AuthProps> = ({ onLogin }) => {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState('');
  const [wechatId, setWechatId] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [codeCooldown, setCodeCooldown] = useState(0);
  const [error, setError] = useState('');

  const submitText = mode === 'register'
    ? '创建账号'
    : '邮箱登录';

  React.useEffect(() => {
    if (codeCooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setCodeCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [codeCooldown]);

  const handleSendEmailCode = async () => {
    setError('');
    const cleanEmail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setError('请输入正确的邮箱地址');
      return;
    }
    setSendingCode(true);
    try {
      const data = await sendEmailCodeApi({ email: cleanEmail, scene: 'register' });
      setCodeCooldown(Math.max(1, Number(data.cooldownSeconds || 60)));
    } catch (err) {
      setError(err instanceof Error ? err.message : '验证码发送失败');
    } finally {
      setSendingCode(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const cleanEmail = email.trim().toLowerCase();
    const cleanPassword = password.trim();
    const isRegister = mode === 'register';

    if (!cleanEmail || !cleanPassword) {
      setError('请输入邮箱和密码');
      return;
    }
    if (cleanPassword.length < 6) {
      setError('密码至少 6 位');
      return;
    }
    if (isRegister && !/^\d{6}$/.test(String(emailCode || '').trim())) {
      setError('请输入 6 位邮箱验证码');
      return;
    }

    setLoading(true);
    try {
      const user = mode === 'register'
        ? await registerApi({
            email: cleanEmail,
            password: cleanPassword,
            name: name.trim() || undefined,
            wechatId: wechatId.trim() || undefined,
            emailCode: emailCode.trim(),
          })
        : await loginApi({
            email: cleanEmail,
            password: cleanPassword,
          });
      onLogin(user);
    } catch (err) {
      setError(err instanceof Error ? err.message : '请求失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf8f5] px-3 pb-6 pt-[calc(env(safe-area-inset-top,0px)+0.7rem)] sm:flex sm:items-center sm:justify-center sm:p-4">
      <div className="relative mx-auto w-full max-w-xl">
        {/* 主卡片 - 国风米色背景 */}
        <div className="bg-[#faf9f7]/95 backdrop-blur-xl rounded-3xl shadow-2xl shadow-[#8b4513]/10 border border-[#e8e4dc] overflow-hidden">
          {/* 标签切换 */}
          <div className="flex border-b border-[#e8e4dc]">
            <button
              type="button"
              onClick={() => {
                setMode('login');
                setError('');
              }}
              className={`flex-1 py-4 text-sm font-semibold transition-all relative ${
                mode === 'login' 
                  ? 'text-[#8b4513]' 
                  : 'text-[#9a9085] hover:text-[#6b5b4f]'
              }`}
            >
              登录
              {mode === 'login' && (
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-[#8b4513] rounded-full" />
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setMode('register');
                setError('');
              }}
              className={`flex-1 py-4 text-sm font-semibold transition-all relative ${
                mode === 'register' 
                  ? 'text-[#8b4513]' 
                  : 'text-[#9a9085] hover:text-[#6b5b4f]'
              }`}
            >
              注册
              {mode === 'register' && (
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-[#8b4513] rounded-full" />
              )}
            </button>
          </div>

          <div className="p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* 注册模式下的额外字段 */}
              {mode === 'register' && (
                <>
                  <div className="relative group">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors">
                      <User className="w-5 h-5" />
                    </div>
                    <input
                      type="text"
                      placeholder="昵称（选填）"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all placeholder:text-slate-400"
                    />
                  </div>
                  <p className="text-xs text-slate-500 px-1">验证码将发送到你的邮箱，用于完成注册。</p>
                </>
              )}

              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors">
                  <Mail className="w-5 h-5" />
                </div>
                <input
                  type="email"
                  placeholder="邮箱地址"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all placeholder:text-slate-400"
                />
              </div>

              {mode === 'register' && (
                <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-3">
                  <div className="relative group min-w-0">
                    <input
                      type="text"
                      placeholder="6位邮箱验证码"
                      value={emailCode}
                      onChange={(e) => setEmailCode(e.target.value)}
                      maxLength={6}
                      className="w-full px-3.5 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-center tracking-[0.28em] font-semibold focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all placeholder:text-slate-400 placeholder:tracking-normal placeholder:font-normal"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleSendEmailCode}
                    disabled={sendingCode || codeCooldown > 0}
                    className="min-w-[102px] px-3 py-3.5 rounded-xl text-xs font-semibold bg-slate-900 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-800 active:scale-95 transition-all whitespace-nowrap"
                  >
                    {sendingCode ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : codeCooldown > 0 ? (
                      `${codeCooldown}s`
                    ) : (
                      '发送验证码'
                    )}
                  </button>
                </div>
              )}

              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors">
                  <Lock className="w-5 h-5" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="密码（至少6位）"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full pl-12 pr-12 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all placeholder:text-slate-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {/* 错误提示 */}
              {error && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-rose-50 border border-rose-100">
                  <div className="w-4 h-4 rounded-full bg-rose-500 flex items-center justify-center shrink-0">
                    <span className="text-white text-xs font-bold">!</span>
                  </div>
                  <p className="text-xs text-rose-600 font-medium">{error}</p>
                </div>
              )}

              {/* 提交按钮 */}
              <button
                type="submit"
                disabled={loading}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#8b4513] to-[#a0522d] py-4 text-sm font-semibold text-white shadow-lg shadow-[#8b4513]/20 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#8b4513]/30 active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:transform-none"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {submitText}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            {/* 底部信息 */}
            <div className="mt-6 text-center">
              <p className="text-xs text-slate-400">
                {mode === 'login' ? '还没有账号？' : '已有账号？'}
                <button
                  type="button"
                  onClick={() => {
                    setMode(mode === 'login' ? 'register' : 'login');
                    setError('');
                  }}
                  className="ml-1 text-indigo-600 font-semibold hover:text-indigo-700 transition-colors"
                >
                  {mode === 'login' ? '立即注册' : '去登录'}
                </button>
              </p>
            </div>
          </div>
        </div>

        {/* 底部安全提示 */}
        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-400">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>您的信息将受到加密保护</span>
        </div>
      </div>
    </div>
  );
};

export default Auth;
